"use client";

import { useState, useEffect } from 'react';
import { useOrganizations, useOrganizationMemberships, type EnrichedOrganizationMember } from '@/hooks/useOrganizations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { DataTable } from '@/components/ui/DataTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { OrgRoleBadge } from '@/components/settings/OrgRoleBadge';
import { formatDistanceToNow, format } from 'date-fns';
import { Building2, Users, Plus, Search, FlaskConical, Shield, FileText, Eye, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Switch } from '@/components/ui/switch';

interface CreateOrgFormData {
  name: string;
  description: string;
}

export default function AdminOrganizationsPage() {
  const { organizations, loading, error, fetchOrganizations, createOrganization, updateOrganization, deleteOrganization } = useOrganizations();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showTestData, setShowTestData] = useState(false);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  const [formData, setFormData] = useState<CreateOrgFormData>({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Fetch member counts for all organizations
  useEffect(() => {
    const fetchMemberCounts = async () => {
      if (!organizations) return;
      
      const counts: Record<string, number> = {};
      for (const org of organizations) {
        try {
          const { data, error } = await supabase.rpc('get_org_member_count' as any, { org_id: org.id });
          if (!error && typeof data === 'number') {
            counts[org.id] = data;
          }
        } catch (err) {
          console.error(`Error fetching member count for org ${org.id}:`, err);
        }
      }
      setMemberCounts(counts);
    };

    fetchMemberCounts();
  }, [organizations]);

  const handleCreateOrganization = async () => {
    try {
      await createOrganization(formData);
      setIsCreateDialogOpen(false);
      setFormData({ name: '', description: '' });
    } catch (err) {
      // Error toast handled by hook
    }
  };

  const handleStatusChange = async (orgId: string, newStatus: string) => {
    try {
      await updateOrganization(orgId, { status: newStatus });
    } catch (err) {
      // Error toast handled by hook
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    if (!confirm('Are you sure you want to delete this organization?')) {
      return;
    }

    try {
      await deleteOrganization(orgId);
    } catch (err) {
      // Error toast handled by hook
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'suspended': return 'destructive';
      default: return 'outline';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredOrganizations = organizations?.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || org.status === statusFilter;
    const matchesTestFilter = showTestData || !(org as any).is_test;
    return matchesSearch && matchesStatus && matchesTestFilter;
  }) || [];

  const columns = [
    {
      key: 'name' as keyof typeof filteredOrganizations[0],
      label: 'Organization',
      sortable: true,
      render: (value: string, org: any) => (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-primary">
              <Building2 className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium flex items-center gap-2">
              {value}
              {org.is_test && (
                <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                  <FlaskConical className="h-3 w-3 mr-1" />
                  Test
                </Badge>
              )}
            </div>
            {org.description && (
              <div className="text-sm text-muted-foreground line-clamp-1">{org.description}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'status' as keyof typeof filteredOrganizations[0],
      label: 'Status',
      sortable: true,
      render: (value: string) => <Badge variant={getStatusBadgeVariant(value)} className="capitalize">{value}</Badge>
    },
    {
      key: 'id' as keyof typeof filteredOrganizations[0],
      label: 'Members',
      render: (value: string) => (
        <div className="flex items-center gap-1 text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>{memberCounts[value] || 0}</span>
        </div>
      )
    },
    {
      key: 'created_at' as keyof typeof filteredOrganizations[0],
      label: 'Created',
      sortable: true,
      render: (value: string) => formatDistanceToNow(new Date(value), { addSuffix: true })
    }
  ];

  const renderActions = (org: any) => (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setSelectedOrg(org);
          setIsDetailDialogOpen(true);
        }}
      >
        View
      </Button>
      <Select
        value={org.status}
        onValueChange={(value) => {
          if (value === 'suspended') {
            if (confirm('Suspend this organization? All members will be prevented from logging in.')) {
              handleStatusChange(org.id, value);
            }
          } else {
            handleStatusChange(org.id, value);
          }
        }}
      >
        <SelectTrigger className="w-[130px] h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="suspended">Suspended</SelectItem>
        </SelectContent>
      </Select>
      <Button
        size="sm"
        variant="destructive"
        onClick={() => handleDeleteOrganization(org.id)}
      >
        Delete
      </Button>
    </div>
  );

  if (loading && !organizations) {
    return (
      <div className="container mx-auto py-8 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8">
        <Card><CardContent className="pt-6"><p className="text-destructive">Error: {error}</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Organizations Management</h1>
          <p className="text-muted-foreground mt-1">Manage platform organizations and members</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Create Organization</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create New Organization</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateOrganization}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/30">
              <FlaskConical className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="show-test" className="text-sm font-normal cursor-pointer">Show test orgs</Label>
              <Switch id="show-test" checked={showTestData} onCheckedChange={setShowTestData} />
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">Showing {filteredOrganizations.length} of {organizations?.length || 0}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable data={filteredOrganizations} columns={columns} actions={renderActions} searchable={false} />
        </CardContent>
      </Card>

      {selectedOrg && (
        <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />{selectedOrg.name}
              </DialogTitle>
            </DialogHeader>
            <OrganizationDetailTabs organization={selectedOrg} memberCount={memberCounts[selectedOrg.id] || 0} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function TestOrgToggle({ organization }: { organization: any }) {
  const [isTest, setIsTest] = useState(organization.is_test || false);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const handleToggle = async (checked: boolean) => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ is_test: checked })
        .eq('id', organization.id);

      if (error) throw error;
      
      setIsTest(checked);
      toast({
        title: checked ? 'Marked as test organization' : 'Unmarked as test organization',
        description: checked 
          ? 'This organization will be excluded from production metrics by default'
          : 'This organization will now be included in production metrics'
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update test status',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Switch
      checked={isTest}
      onCheckedChange={handleToggle}
      disabled={updating}
    />
  );
}

function OrgAdminToggle({ organization }: { organization: any }) {
  const [isEnabled, setIsEnabled] = useState(organization.org_admin_enabled || false);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const handleToggle = async (checked: boolean) => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ org_admin_enabled: checked })
        .eq('id', organization.id);

      if (error) throw error;
      
      setIsEnabled(checked);
      toast({
        title: checked ? 'Org Admin features enabled' : 'Org Admin features disabled',
        description: checked 
          ? 'Organization owners will now see the Org Admin option in the sidebar'
          : 'The Org Admin option has been hidden from the sidebar'
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update Org Admin setting',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Switch
      checked={isEnabled}
      onCheckedChange={handleToggle}
      disabled={updating}
    />
  );
}

function OrganizationDetailTabs({ organization, memberCount }: { organization: any; memberCount: number }) {
  const { memberships, loading, error, updateMemberRole, removeMember } = useOrganizationMemberships(organization.id);
  const { toast } = useToast();
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [vendorProfile, setVendorProfile] = useState<any>(null);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Fetch vendor profile for documents tab
  useEffect(() => {
    const fetchVendorProfile = async () => {
      setDocumentsLoading(true);
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select(`
          id, business_name, verification_level,
          w9_document_url, w9_uploaded_at,
          business_registration_url, business_registration_uploaded_at,
          cgl_insurance_url, cgl_insurance_uploaded_at, cgl_insurance_expiry_date
        `)
        .eq('organization_id', organization.id)
        .maybeSingle();
      
      if (!error && data) {
        setVendorProfile(data);
      }
      setDocumentsLoading(false);
    };
    
    fetchVendorProfile();
  }, [organization.id]);

  const handleViewDocument = async (storagePath: string) => {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(storagePath, 3600);
    
    if (error) {
      toast({ title: 'Error', description: 'Failed to generate document URL', variant: 'destructive' });
      return;
    }
    window.open(data.signedUrl, '_blank');
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'suspended': return 'destructive';
      default: return 'outline';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleRoleChange = async (userId: string, newRole: 'owner' | 'super_user' | 'member') => {
    try {
      await updateMemberRole(userId, newRole);
      toast({ title: 'Success', description: 'Member role updated' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update role', variant: 'destructive' });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!confirm('Remove this member?')) return;
    try {
      await removeMember(userId);
      toast({ title: 'Success', description: 'Member removed' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove member', variant: 'destructive' });
    }
  };

  const filteredMembers = memberships?.filter(member => {
    const matchesSearch = member.display_name?.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
                         member.email?.toLowerCase().includes(memberSearchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.org_role === roleFilter;
    return matchesSearch && matchesRole;
  }) || [];

  const memberColumns = [
    {
      key: 'display_name' as keyof EnrichedOrganizationMember,
      label: 'Member',
      sortable: true,
      render: (value: string, member: EnrichedOrganizationMember) => (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8"><AvatarFallback>{getInitials(value)}</AvatarFallback></Avatar>
          <div>
            <div className="font-medium">{value}</div>
            <div className="text-sm text-muted-foreground">{member.email}</div>
          </div>
        </div>
      )
    },
    { key: 'org_role' as keyof EnrichedOrganizationMember, label: 'Org Role', sortable: true, render: (value: string) => <OrgRoleBadge role={value} /> },
    { key: 'platform_role' as keyof EnrichedOrganizationMember, label: 'Platform Role', sortable: true, render: (value: string) => <Badge variant="outline" className="capitalize">{value}</Badge> },
    { key: 'created_at' as keyof EnrichedOrganizationMember, label: 'Joined', sortable: true, render: (value: string) => formatDistanceToNow(new Date(value), { addSuffix: true }) }
  ];

  const renderMemberActions = (member: EnrichedOrganizationMember) => (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          const newRole = prompt('Enter role (owner, super_user, member):', member.org_role);
          if (newRole && ['owner', 'super_user', 'member'].includes(newRole)) {
            handleRoleChange(member.user_id, newRole as 'owner' | 'super_user' | 'member');
          }
        }}
      >
        Change Role
      </Button>
      <Button
        size="sm"
        variant="destructive"
        onClick={() => handleRemoveMember(member.user_id)}
      >
        Remove
      </Button>
    </div>
  );

  const roleBreakdown = {
    owner: memberships?.filter(m => m.org_role === 'owner').length || 0,
    super_user: memberships?.filter(m => m.org_role === 'super_user').length || 0,
    member: memberships?.filter(m => m.org_role === 'member').length || 0
  };

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="members">Members {memberCount > 0 && <Badge variant="secondary" className="ml-2">{memberCount}</Badge>}</TabsTrigger>
        <TabsTrigger value="documents">Documents</TabsTrigger>
        <TabsTrigger value="settings">Settings</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Card><CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Status</CardTitle></CardHeader><CardContent><Badge variant={getStatusBadgeVariant(organization.status)} className="capitalize">{organization.status}</Badge></CardContent></Card>
          <Card><CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Members</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{memberCount}</div></CardContent></Card>
          <Card><CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Created</CardTitle></CardHeader><CardContent><div className="text-sm">{formatDistanceToNow(new Date(organization.created_at), { addSuffix: true })}</div></CardContent></Card>
        </div>
        <Card><CardHeader><CardTitle>Member Breakdown</CardTitle></CardHeader><CardContent className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Owners</span><Badge variant="default">{roleBreakdown.owner}</Badge></div>
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Super Users</span><Badge variant="secondary">{roleBreakdown.super_user}</Badge></div>
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Members</span><Badge variant="outline">{roleBreakdown.member}</Badge></div>
        </CardContent></Card>
      </TabsContent>

      <TabsContent value="members" className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." value={memberSearchTerm} onChange={(e) => setMemberSearchTerm(e.target.value)} className="pl-9" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="super_user">Super User</SelectItem>
              <SelectItem value="member">Member</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : error ? (
          <Card><CardContent className="pt-6"><p className="text-destructive">Error: {error}</p></CardContent></Card>
        ) : filteredMembers.length === 0 ? (
          <Card><CardContent className="pt-6 text-center"><Users className="h-12 w-12 mx-auto text-muted-foreground mb-2" /><p className="text-muted-foreground">{memberSearchTerm || roleFilter !== 'all' ? 'No matches' : 'No members yet'}</p></CardContent></Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">Showing {filteredMembers.length} of {memberships?.length || 0}</p>
            <DataTable data={filteredMembers} columns={memberColumns} actions={renderMemberActions} searchable={false} />
          </>
        )}
      </TabsContent>

      <TabsContent value="documents" className="space-y-4">
        {documentsLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : !vendorProfile ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No vendor profile found for this organization</p>
              <p className="text-sm text-muted-foreground mt-1">This organization may not be a vendor</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Verification Documents</h4>
              <Badge variant="outline" className="capitalize">
                {vendorProfile.verification_level || 'Unverified'}
              </Badge>
            </div>
            
            <div className="space-y-3">
              {/* W-9 Document */}
              <Card>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">W-9 Form</p>
                      <p className="text-sm text-muted-foreground">
                        {vendorProfile.w9_document_url 
                          ? `Uploaded ${formatDistanceToNow(new Date(vendorProfile.w9_uploaded_at), { addSuffix: true })}`
                          : 'Not uploaded'}
                      </p>
                    </div>
                  </div>
                  {vendorProfile.w9_document_url && (
                    <Button size="sm" variant="outline" onClick={() => handleViewDocument(vendorProfile.w9_document_url)}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Business Registration */}
              <Card>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Business Registration</p>
                      <p className="text-sm text-muted-foreground">
                        {vendorProfile.business_registration_url 
                          ? `Uploaded ${formatDistanceToNow(new Date(vendorProfile.business_registration_uploaded_at), { addSuffix: true })}`
                          : 'Not uploaded'}
                      </p>
                    </div>
                  </div>
                  {vendorProfile.business_registration_url && (
                    <Button size="sm" variant="outline" onClick={() => handleViewDocument(vendorProfile.business_registration_url)}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* CGL Insurance */}
              <Card>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">CGL Insurance Certificate</p>
                      <p className="text-sm text-muted-foreground">
                        {vendorProfile.cgl_insurance_url 
                          ? `Uploaded ${formatDistanceToNow(new Date(vendorProfile.cgl_insurance_uploaded_at), { addSuffix: true })}`
                          : 'Not uploaded'}
                      </p>
                      {vendorProfile.cgl_insurance_expiry_date && (
                        <p className="text-xs text-muted-foreground">
                          Expires: {format(new Date(vendorProfile.cgl_insurance_expiry_date), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                  </div>
                  {vendorProfile.cgl_insurance_url && (
                    <Button size="sm" variant="outline" onClick={() => handleViewDocument(vendorProfile.cgl_insurance_url)}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Document Status Summary */}
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Documents Uploaded</span>
                  <span className="font-medium">
                    {[vendorProfile.w9_document_url, vendorProfile.business_registration_url, vendorProfile.cgl_insurance_url].filter(Boolean).length} / 3
                  </span>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </TabsContent>

      <TabsContent value="settings">
        <Card>
          <CardHeader>
            <CardTitle>Organization Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <Label className="text-base font-medium">Test Organization</Label>
                  <p className="text-sm text-muted-foreground">Mark this organization as test data to exclude from production metrics</p>
                </div>
              </div>
              <TestOrgToggle organization={organization} />
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <Label className="text-base font-medium">Enable Org Admin Features</Label>
                  <p className="text-sm text-muted-foreground">Allow organization owners to access Org Admin settings in the sidebar</p>
                </div>
              </div>
              <OrgAdminToggle organization={organization} />
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
